import { useRef, useState } from "react";

const hasFiles = (e: React.DragEvent) =>
  Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** Files carried by a paste event (e.g. a screenshot), or null if text-only. */
export function clipboardFiles(e: React.ClipboardEvent): FileList | null {
  const files = e.clipboardData?.files;
  return files && files.length > 0 ? files : null;
}

export interface FileDropTarget {
  /** True while files are dragged over the target. */
  isOver: boolean;
  /** Spread onto the target element. */
  props: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

/**
 * Makes an element a native file drop target. Events stop propagating so a
 * target nested inside another (card inside list) wins over its parent.
 * Ignores non-file drags (e.g. dnd-kit card drags use pointer events anyway).
 */
export function useFileDrop(
  onDrop: (files: FileList) => void,
  enabled = true,
): FileDropTarget {
  const depth = useRef(0);
  const [isOver, setIsOver] = useState(false);

  return {
    isOver,
    props: {
      onDragEnter(e) {
        if (!enabled || !hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        depth.current += 1;
        setIsOver(true);
      },
      onDragOver(e) {
        if (!enabled || !hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
      },
      onDragLeave(e) {
        if (!enabled || !hasFiles(e)) return;
        e.stopPropagation();
        depth.current -= 1;
        if (depth.current <= 0) {
          depth.current = 0;
          setIsOver(false);
        }
      },
      onDrop(e) {
        if (!enabled || !hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
        depth.current = 0;
        setIsOver(false);
        if (e.dataTransfer.files?.length) onDrop(e.dataTransfer.files);
      },
    },
  };
}
